import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Autocomplete,
  Typography,
  Button,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCardIcon from "@mui/icons-material/AddCard";
import Diversity2Icon from "@mui/icons-material/Diversity2";
import LabelIcon from "@mui/icons-material/Label";
import { BASE_URL } from "../../constants";
import { toast } from "react-toastify";

const AccountCreateDialog = ({
  open,
  onClose,
  setGroups,
  groups,
  onAccountCreated, // 🔥 ADD THIS
}) => {
  // 🔥 REFS
  const groupRef = useRef(null);
  const accountRef = useRef(null);
  const accountTypeRef = useRef(null);
  const createGroupRef = useRef(null);
  const createBtnRef = useRef(null);

  // 🔥 STATE
  const [mode, setMode] = useState("account"); // account | group

  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [newGroupName, setNewGroupName] = useState("");

  // 🔥 OPTIONS
  const enhancedGroups = [
    { name: "F2 → Create New Group", isCreate: true },
    ...groups,
  ];

  // 🔥 AUTO FOCUS
  useEffect(() => {
    if (open && mode === "account") {
      setTimeout(() => {
        groupRef.current?.focus();
      }, 150);
    }
  }, [open, mode]);

  // 🔥 F2 HANDLER
  useEffect(() => {
    const handleF2 = (e) => {
      if (!open) return;

      if (e.key === "F2") {
        e.preventDefault();
        setMode("group");

        setTimeout(() => {
          createGroupRef.current?.focus();
        }, 100);
      }
    };

    window.addEventListener("keydown", handleF2);
    return () => window.removeEventListener("keydown", handleF2);
  }, [open]);

  // 🔥 ESC BACK
  useEffect(() => {
    const handleEsc = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();

        if (mode === "group") {
          // 🔥 BACK TO ACCOUNT MODE
          setMode("account");

          setTimeout(() => {
            groupRef.current?.focus();
          }, 100);
        } else {
          // 🔥 CLOSE DIALOG ONLY IN ACCOUNT MODE
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleEsc, true); // 🔥 use capture phase
    return () => window.removeEventListener("keydown", handleEsc, true);
  }, [mode, open]);

  // 🔥 NAVIGATION
  const moveFocus = (direction, current) => {
    const order = ["group", "account", "accountType"];
    const index = order.indexOf(current);

    if (index === -1) return;

    const nextIndex = direction === "down" ? index + 1 : index - 1;

    const next = order[nextIndex];

    setTimeout(() => {
      if (next === "group") groupRef.current?.focus();
      if (next === "account") accountRef.current?.focus();
      if (next === "accountType") accountTypeRef.current?.focus();
    }, 50);
  };

  const createGroupAPI = async () => {
    if (!newGroupName) return;

    try {
      const res = await fetch(`${BASE_URL}api/user/common/group/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: newGroupName }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.message);
        return;
      }

      const newGroup = data.group;

      // 🔥 update UI
      setGroups((prev) => [...prev, newGroup]);
      setSelectedGroup(newGroup);

      setNewGroupName("");
      setMode("account");

      setTimeout(() => {
        accountRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // 🔥 Only when dialog is open
      if (!open) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        e.stopPropagation();

        // 🔥 Force blur so latest input value is committed
        const active = document.activeElement;
        if (active && typeof active.blur === "function") {
          active.blur();
        }

        // 🔥 Trigger button click
        setTimeout(() => {
          createBtnRef.current?.click();
        }, 50);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const createAccountAPI = async () => {
    if (!accountName || !accountType || !selectedGroup?._id) {
      alert("Please fill all fields");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}api/user/common/account/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: accountName,
          type: accountType,
          groupId: selectedGroup._id,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.message);
        return;
      }

      const newAccount = data.account;

      // 🔥 send to parent (ManualVoucher)
      onAccountCreated?.(newAccount);

      // 🔥 reset
      setAccountName("");
      setAccountType("");
      setSelectedGroup(null);

      // 🔥 close dialog
      onClose();
      toast.success("Account Created ✔");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "escapeKeyDown") return;
        onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* 🔥 HEADER */}
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(to right, #0f172a, #1e293b)",
          color: "#fff",
          py: 1.5,
          fontSize: 16,
        }}
      >
        {mode === "group" ? (
          <>
            <Diversity2Icon sx={{ fontSize: 20 }} />
            Create Group
          </>
        ) : (
          <>
            <AddCardIcon sx={{ fontSize: 20 }} />
            Create Account
          </>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: "#f8fafc",
          py: 3,
        }}
      >
        {mode === "group" ? (
          // ================= GROUP MODE =================
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* BACK */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: "#eb2525",
                fontWeight: 600,
                width: "fit-content",
              }}
              onClick={() => {
                setMode("account");
                setTimeout(() => groupRef.current?.focus(), 100);
              }}
            >
              <ArrowBackIcon fontSize="small" />
              Back
            </Box>

            {/* GROUP INPUT */}
            <TextField
              label="Group Name"
              inputRef={createGroupRef}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  createGroupAPI();
                }
              }}
            />

            {/* SUBMIT */}
            <Button variant="contained" onClick={createGroupAPI}>
              Create Group
            </Button>
          </Box>
        ) : (
          // ================= ACCOUNT MODE =================
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* GROUP SELECT */}
            <Autocomplete
              fullWidth
              autoHighlight
              options={enhancedGroups}
              value={selectedGroup}
              getOptionLabel={(opt) => opt?.name || ""}
              getOptionDisabled={(opt) => opt.isCreate}
              isOptionEqualToValue={(opt, val) => opt.name === val.name}
              filterOptions={(options, state) => {
                const input = state.inputValue.toLowerCase();
                return options.filter((opt) => {
                  if (opt.isCreate) return true;
                  return opt.name.toLowerCase().includes(input);
                });
              }}
              onChange={(e, val) => {
                if (!val) return;

                if (val.isCreate) {
                  setMode("group");
                  setTimeout(() => {
                    createGroupRef.current?.focus();
                  }, 100);
                  return;
                }

                setSelectedGroup(val);

                setTimeout(() => {
                  accountRef.current?.focus();
                }, 100);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Group / F2 new"
                  size="small"
                  inputRef={groupRef}
                  onKeyDown={(e) => {
                    // if (e.key === "ArrowDown") {
                    //   e.preventDefault();
                    //   moveFocus("down", "group");
                    // }
                    if (e.key === "Enter") {
                      if (!selectedGroup) return;
                      e.preventDefault();
                      accountRef.current?.focus();
                    }
                  }}
                />
              )}
            />

            {/* ACCOUNT NAME */}
            <TextField
              label="Account Name"
              inputRef={accountRef}
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  moveFocus("down", "account");
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  moveFocus("up", "account");
                }

                if (e.key === "Enter") {
                  if (!accountName) return;
                  e.preventDefault();
                  accountTypeRef.current?.focus();
                }
              }}
            />

            {/* ACCOUNT TYPE */}
            <TextField
              select
              label="Account Type"
              inputRef={accountTypeRef}
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              fullWidth
              size="small"
              SelectProps={{
                native: true,
              }}
              InputProps={{
                startAdornment: <LabelIcon sx={{ mr: 1, color: "#64748b" }} />,
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  moveFocus("up", "accountType");
                }

                if (e.key === "Enter") {
                  e.preventDefault();
                  console.log("Save account", {
                    accountName,
                    accountType,
                    selectedGroup,
                  });
                }
              }}
            >
              <option value="">Select Account Type</option>
              <option value="Assets">Assets</option>
              <option value="Liabilities">Liabilities</option>
              <option value="Equity">Equity</option>
              <option value="Operating Revenue">Operating Revenue</option>
              <option value="Operating Expenses">Operating Expenses</option>
            </TextField>
            {/* SUBMIT */}
            <Button
              ref={createBtnRef}
              variant="contained"
              sx={{
                alignSelf: "flex-end",
                mt: 1,
                borderRadius: 2,
                textTransform: "none",
              }}
              onClick={createAccountAPI}
            >
              Create Account
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountCreateDialog;
